import { z } from 'zod'

import { applicationSchema, type Application } from '#admin/features/exhibitor/api.ts'
import { api } from '#admin/lib/api-client.ts'

/**
 * The operator routes the onboarding desk needs beside the ones `../api.ts` already has: open an
 * application for a company, submit it, and name the staff on the stand's passes.
 */

const base = '/v1/exhibitor/admin/applications'

export type OpenApplicationInput = {
  companyId: string
  contactName: string
  contactEmail: string
  contactPhone?: string
  boothTypeId: string
  currency: string
}

export function openApplication(input: OpenApplicationInput): Promise<Application> {
  return api.post(base, { schema: applicationSchema, body: input })
}

export function submitApplication(id: string): Promise<Application> {
  return api.post(`${base}/${id}/submit`, { schema: applicationSchema, body: {} })
}

export type PassHolder = {
  holderName: string
  email: string
  jobTitle?: string
  /** A convention pass or an exhibitor pass. Absent: the type that the issue names. */
  ticketTypeId?: string
}

export function claimPass(applicationId: string, holder: PassHolder): Promise<unknown> {
  return api.post(`${base}/${applicationId}/convention-passes`, {
    schema: z.object({ id: z.string() }),
    body: holder,
  })
}

/**
 * Issues a badge for each named pass. A pass that names no type gets `ticketTypeId`. There is one
 * registration for each pass type; `registrationId` is the last one. Null when it issued none.
 */
export function issuePasses(
  applicationId: string,
  ticketTypeId?: string,
): Promise<{ issued: number; registrationId: string | null; registrationIds: string[] }> {
  return api.post(`${base}/${applicationId}/convention-passes/issue`, {
    schema: z.object({
      issued: z.number(),
      // `.default(…)`: the console can be deployed ahead of the API.
      registrationId: z.string().nullable().default(null),
      registrationIds: z.array(z.string()).default([]),
    }),
    body: ticketTypeId === undefined ? {} : { ticketTypeId },
  })
}

/** Raises the service order's invoice, and gives its id. */
export function invoiceOrder(serviceOrderId: string): Promise<string | null> {
  return api
    .post(`/v1/exhibitor/admin/service-orders/${serviceOrderId}/invoice`, {
      schema: z.object({ invoiceId: z.string().nullable() }),
      body: {},
    })
    .then((order) => order.invoiceId)
}

export const badgeTypeSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  // `.default(…)`: the console can be deployed ahead of the API.
  category: z.string().default('exhibitor_pass'),
  description: z.string().nullable().default(null),
})
export type BadgeType = z.infer<typeof badgeTypeSchema>

/** The free badge types a pass can be issued against. Empty when `ticket` is off. */
export function fetchBadgeTypes(signal?: AbortSignal): Promise<{ items: BadgeType[] }> {
  return api.get('/v1/exhibitor/admin/convention-passes/badge-types', {
    schema: z.object({ items: z.array(badgeTypeSchema) }),
    ...(signal ? { signal } : {}),
  })
}
